import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Lock, Unlock, Fingerprint } from "lucide-react";

// ============== WebAuthn Helpers ==============

function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - base64.length % 4) % 4);
  const binary = atob(base64 + padding);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function isBiometricSupported() {
  if (!window.PublicKeyCredential) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

// Simple device fingerprint to avoid re-registering on same device
function getDeviceId() {
  let id = localStorage.getItem("_biometric_device_id");
  if (!id) {
    id = "dev_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("_biometric_device_id", id);
  }
  return id;
}

export default function AppLockScreen({ onUnlock, pageKey = null, biometricRegistered = false }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [biometricSupported, setBiometricSupported] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [biometricFailCount, setBiometricFailCount] = useState(0);
  // True when this device has no usable passkey — either it never enrolled, or the one it had
  // is gone (app reinstalled, passkey deleted, Chrome data cleared). Drives the manual
  // "Set up fingerprint unlock" button, which is the only way back once the credential the
  // server knows about no longer exists on the device.
  const [needsSetup, setNeedsSetup] = useState(false);
  const autoAttemptedRef = useRef(false);
  const registeringRef = useRef(false);

  const MAX_BIOMETRIC_FAILS = 3;

  // Check if biometric is supported on this device
  useEffect(() => {
    isBiometricSupported().then((supported) => {
      setBiometricSupported(supported);
      if (supported && !localStorage.getItem(`_biometric_registered_${getDeviceId()}`)) {
        setNeedsSetup(true);
      }
    });
  }, []);

  // ============== Biometric Registration (silent, after PIN) ==============

  // force: skip the "already registered on this device" short-circuit. Needed because that flag
  // lives in localStorage while the passkey lives in the platform authenticator — reinstalling the
  // app can wipe one and not the other, and a stale flag would otherwise block re-enrolment forever.
  // interactive: surface errors to the user instead of failing quietly (manual setup button).
  const registerBiometric = useCallback(async ({ force = false, interactive = false } = {}) => {
    if (registeringRef.current) return;
    registeringRef.current = true;
    if (interactive) setBiometricLoading(true);

    try {
      // Check if this device already registered
      const deviceId = getDeviceId();
      const registered = localStorage.getItem(`_biometric_registered_${deviceId}`);
      if (registered && !force) return; // Already registered on this device

      // 1. Get registration options
      const optionsRes = await axios.get(`${API}/users/me/app-lock-biometric-register-options`, { withCredentials: true });
      const options = optionsRes.data;

      // 2. Convert from base64url to ArrayBuffer
      const publicKeyOptions = {
        challenge: base64urlToBuffer(options.challenge),
        rp: options.rp,
        user: {
          ...options.user,
          id: base64urlToBuffer(options.user.id),
        },
        pubKeyCredParams: options.pubKeyCredParams,
        timeout: options.timeout || 60000,
        attestation: options.attestation || "none",
        authenticatorSelection: options.authenticatorSelection,
      };

      if (options.excludeCredentials) {
        publicKeyOptions.excludeCredentials = options.excludeCredentials.map(c => ({
          id: base64urlToBuffer(c.id),
          type: c.type,
          transports: c.transports,
        }));
      }

      // 3. Create credential (triggers biometric enrollment)
      const credential = await navigator.credentials.create({ publicKey: publicKeyOptions });

      // 4. Send to server
      const credentialJSON = {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        response: {
          clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
          attestationObject: bufferToBase64url(credential.response.attestationObject),
        },
        type: credential.type,
        authenticatorAttachment: credential.authenticatorAttachment || "platform",
      };

      if (credential.response.getTransports) {
        credentialJSON.response.transports = credential.response.getTransports();
      }

      await axios.post(`${API}/users/me/app-lock-biometric-register`, credentialJSON, { withCredentials: true });

      // Mark this device as registered
      localStorage.setItem(`_biometric_registered_${deviceId}`, "1");
      setNeedsSetup(false);
      setBiometricFailCount(0);
      toast.success("Biometric unlock enabled for this device");
    } catch (err) {
      // InvalidStateError means credential already exists on this device — mark as registered
      if (err?.name === "InvalidStateError") {
        localStorage.setItem(`_biometric_registered_${getDeviceId()}`, "1");
        setNeedsSetup(false);
        if (interactive) toast.success("This device is already set up for biometric unlock");
        return;
      }
      const cancelled = err?.name === "NotAllowedError" || err?.name === "AbortError";
      if (!cancelled) console.error("Biometric registration failed:", err);
      if (interactive) {
        toast.error(
          cancelled
            ? "Setup cancelled"
            : err?.response?.data?.detail || "Could not set up biometric unlock on this device"
        );
      }
    } finally {
      registeringRef.current = false;
      if (interactive) setBiometricLoading(false);
    }
  }, []);

  const silentBiometricRegister = useCallback(() => registerBiometric(), [registerBiometric]);

  // ============== Biometric Authentication ==============

  const attemptBiometricUnlock = useCallback(async () => {
    if (!biometricSupported || biometricLoading) return;
    if (biometricFailCount >= MAX_BIOMETRIC_FAILS) return;
    setBiometricLoading(true);
    setError("");

    try {
      // 1. Get auth options from server
      const optionsRes = await axios.get(`${API}/users/me/app-lock-biometric-auth-options`, { withCredentials: true });
      const options = optionsRes.data;

      // 2. Convert from base64url to ArrayBuffer
      const publicKeyOptions = {
        challenge: base64urlToBuffer(options.challenge),
        timeout: options.timeout || 60000,
        rpId: options.rpId,
        userVerification: options.userVerification || "required",
      };

      if (options.allowCredentials) {
        publicKeyOptions.allowCredentials = options.allowCredentials.map(c => ({
          id: base64urlToBuffer(c.id),
          type: c.type,
          transports: c.transports,
        }));
      }

      // 3. Trigger biometric prompt
      const assertion = await navigator.credentials.get({ publicKey: publicKeyOptions });

      // 4. Send assertion to server
      const assertionJSON = {
        id: assertion.id,
        rawId: bufferToBase64url(assertion.rawId),
        response: {
          clientDataJSON: bufferToBase64url(assertion.response.clientDataJSON),
          authenticatorData: bufferToBase64url(assertion.response.authenticatorData),
          signature: bufferToBase64url(assertion.response.signature),
          userHandle: assertion.response.userHandle ? bufferToBase64url(assertion.response.userHandle) : null,
        },
        type: assertion.type,
        authenticatorAttachment: assertion.authenticatorAttachment || "platform",
      };

      await axios.post(`${API}/users/me/app-lock-biometric-verify`, assertionJSON, { withCredentials: true });
      onUnlock();
    } catch (err) {
      // A client-side NotAllowedError/AbortError is either a user cancellation OR Android's
      // "no passkeys available" — the two are indistinguishable here. Offer manual setup either
      // way: if they simply cancelled, the extra button is harmless; if the passkey is really
      // gone, it is the only route back.
      if (err?.name === "NotAllowedError" || err?.name === "AbortError") {
        localStorage.removeItem(`_biometric_registered_${getDeviceId()}`);
        setNeedsSetup(true);
      } else {
        // For "no credentials on this device" type errors, don't show confusing messages
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail || "";
        if (status === 400 && (detail.includes("No biometric") || detail.includes("Unknown credential"))) {
          // This device doesn't have a registered credential — clear local flag so it re-registers after PIN
          localStorage.removeItem(`_biometric_registered_${getDeviceId()}`);
          setNeedsSetup(true);
          // Don't show error, just let them use PIN
        } else {
          console.error("Biometric auth error:", err);
          setBiometricFailCount(prev => prev + 1);
        }
      }
    } finally {
      setBiometricLoading(false);
    }
  }, [biometricSupported, biometricLoading, biometricFailCount, onUnlock]);

  // Auto-trigger biometric prompt on mount if supported and registered
  useEffect(() => {
    if (biometricRegistered && biometricSupported && !autoAttemptedRef.current) {
      autoAttemptedRef.current = true;
      const timer = setTimeout(() => attemptBiometricUnlock(), 300);
      return () => clearTimeout(timer);
    }
  }, [biometricRegistered, biometricSupported]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============== PIN Submit ==============

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pin.length < 4) {
      setError("PIN must be at least 4 digits");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await axios.post(`${API}/users/me/verify-app-lock`, { pin_code: pin }, { withCredentials: true });
      toast.success("Unlocked");

      // After successful PIN, silently register biometric for this device if supported
      if (biometricSupported) {
        // Don't await — let it happen in the background after unlock
        silentBiometricRegister();
      }

      onUnlock();
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid PIN");
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  // ============== UI ==============

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-900 border-amber-700 border-2">
        <CardHeader className="text-center">
          <div className="mx-auto mb-3 w-16 h-16 rounded-full bg-amber-900/50 flex items-center justify-center">
            <Lock className="w-8 h-8 text-amber-400" />
          </div>
          <CardTitle className="text-xl text-amber-400">App Locked</CardTitle>
          <p className="text-sm text-slate-400 mt-2">
            {pageKey
              ? "This page requires verification to access"
              : "Please verify to continue"
            }
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Biometric unlock button */}
          {biometricRegistered && biometricSupported && biometricFailCount < MAX_BIOMETRIC_FAILS && (
            <div className="space-y-3">
              {biometricLoading ? (
                <div className="text-center py-3">
                  <Fingerprint className="w-8 h-8 text-amber-400 mx-auto mb-2 animate-pulse" />
                  <p className="text-sm text-slate-400">Verifying...</p>
                </div>
              ) : (
                <Button
                  onClick={attemptBiometricUnlock}
                  className="w-full bg-amber-600 hover:bg-amber-700 h-12"
                >
                  <Fingerprint className="w-5 h-5 mr-2" />
                  Unlock with Biometrics
                </Button>
              )}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-slate-700" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-slate-900 px-2 text-slate-500">or use PIN</span>
                </div>
              </div>
            </div>
          )}

          {/* Manual biometric enrolment. The silent post-PIN registration is keyed to a
              localStorage flag, so it never retries once that flag is set — after an app
              reinstall the flag can outlive the passkey itself. This is the way out. */}
          {biometricSupported && needsSetup && !biometricLoading && (
            <Button
              type="button"
              variant="outline"
              onClick={() => registerBiometric({ force: true, interactive: true })}
              className="w-full border-amber-700 text-amber-400 hover:bg-amber-900/30 h-11"
            >
              <Fingerprint className="w-5 h-5 mr-2" />
              Set up fingerprint unlock
            </Button>
          )}

          {/* PIN form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-slate-300">PIN Code</Label>
              {/* Hidden fake field to prevent password managers from triggering */}
              <input type="text" name="fake_field" autoComplete="off" style={{ display: 'none' }} tabIndex={-1} />
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                name="app-lock-pin-entry"
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore
                data-form-type="other"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
                placeholder="Enter PIN..."
                className="bg-slate-800 border-slate-700 text-center text-2xl tracking-[0.5em] font-mono"
                style={{ WebkitTextSecurity: 'disc', textSecurity: 'disc' }}
                autoFocus={!biometricRegistered}
                maxLength={8}
              />
            </div>
            {error && (
              <p className="text-red-400 text-sm text-center">{error}</p>
            )}
            <Button
              type="submit"
              className={`w-full ${biometricRegistered ? 'bg-slate-700 hover:bg-slate-600' : 'bg-amber-600 hover:bg-amber-700'}`}
              disabled={loading || pin.length < 4}
            >
              {loading ? (
                "Verifying..."
              ) : (
                <>
                  <Unlock className="w-4 h-4 mr-2" />
                  Unlock with PIN
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
